import React, { ReactNode, useEffect, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import { GoalDisplay } from './types';
import {
  Toast,
  formatGoalToString,
  GOAL_JUSTIFICATION_SIZE_LIMIT,
  GoalTab,
  GoalPage,
  getTimestampTranslation,
} from './helper';
import styles from './goals.module.css';
import Toaster from '@amzn/meridian/toaster';
import Alert from '@amzn/meridian/alert';
import { getLocaleStringFormatted } from '../../translations';
import { StateContext } from '../../GlobalStateStore';
import useCustomTracking from '../../Util/useCustomTracking';

export interface GoalJustificationModalProps {
  cscEntityPrefix: string;
  isOpen: boolean;
  setIsOpen: any;
  goal: GoalDisplay;
  isNewJustification: boolean;
  changeFlag: any;
  tab: GoalTab;
  page: GoalPage;
}

const GoalJustificationModal: React.FC<GoalJustificationModalProps> = ({
  cscEntityPrefix,
  isOpen,
  setIsOpen,
  goal,
  isNewJustification,
  changeFlag,
  tab,
  page,
}: GoalJustificationModalProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const intl = useIntl();
  const [createGoalJustification, setCreateGoalJustification] = useState('');
  const [createGoalJustificationState, setCreateGoalJustificationState] =
    useState(DataState.INITIALIZED);
  const [addJustificationValue, setAddJustificationValue] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const locale = getLocaleStringFormatted();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUsername, requesterUserID } = inspireOptions.data;

  const commonClickstreamData = {
    'data-csa-c-type': 'button',
    'data-csa-c-tag1': page,
    'data-csa-c-tag2': 'Goal Setting',
    'data-csa-c-tag3': `${tab == GoalTab.ASSOCIATE ? 'Associate' : 'Team'} Tab`,
    'data-csa-c-tag4': 'Goal Details',
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };

  const [
    GoalCloseDetailsClickstreamTrackingTag,
    emitGoalCloseDetailsClickstreamTags,
  ] = useCustomTracking(
    `${page}-goal-setting-close-details-for-${goal.userName}-${goal.goalID}`,
    `${page}-goal-setting-close-details`,
    {
      ...commonClickstreamData,
    }
  );

  const onClickAddJustificationButton = async () => {
    if (addJustificationValue.length > GOAL_JUSTIFICATION_SIZE_LIMIT) {
      return;
    }

    const queryVariables = apiNaws.prepareGoalJustificationInput(
      requesterUsername,
      goal.goalID,
      addJustificationValue,
      goal.userID
    );

    await apiNaws.addGoalJustification(
      queryVariables,
      setCreateGoalJustification,
      setCreateGoalJustificationState
    );
  };
  let toastId = 0;

  const onClickClose = () => {
    setAddJustificationValue('');
    setIsOpen(false);
  };

  const onCloseToast = (id: string) =>
    setToasts(toasts.filter(t => t.id !== id));

  const onOpenToast = (type: 'success' | 'error') => {
    const id = `${++toastId}`;
    setToasts(toasts.concat({ id, type }));
    setTimeout(() => onCloseToast(id), 5000);
  };

  useEffect(() => {
    if (createGoalJustificationState === DataState.LOADED) {
      onOpenToast('success');
      onClickClose();
      setAddJustificationValue('');
      changeFlag(goal.goalID);
    }
    if (createGoalJustificationState === DataState.ERROR) onOpenToast('error');
  }, [createGoalJustificationState]);

  return (
    <>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {toast => (
          <>
            {toast.type === 'success' && (
              <Alert toast={true} onClose={toast.onClose} type='success'>
                <FormattedMessage
                  id='cs_perfportal_goal_justification_add_success'
                  defaultMessage='Details successfully added to the goal.'
                />
              </Alert>
            )}
            {toast.type === 'error' && (
              <Alert toast={true} onClose={toast.onClose} type='error'>
                <FormattedMessage
                  id='cs_perfportal_goal_justification_add_fail'
                  defaultMessage='System error. Unable to add details to the goal. Try Again.'
                />
              </Alert>
            )}
          </>
        )}
      </Toaster>
      <GoalCloseDetailsClickstreamTrackingTag />
      <Modal
        title={intl.formatMessage({
          id: 'cs_perfportal_goal_justification_create_header',
          defaultMessage: `Give details about goal not met`,
        })}
        open={isOpen}
        onClose={() => {
          onClickClose();
          emitGoalCloseDetailsClickstreamTags();
        }}
        scrollContainer='viewport'
        closeLabel='Close'
        aria-describedby='modal-description'
        width={'809px'}
        bodySpacingInset='450'
        data-cy='goal-justification-modal'
      >
        <Box height='fill' width='fill' spacingInset='300'>
          <Column spacingInset='300 none'>
            <Text type='h100' data-testid='goal-label'>
              {formatGoalToString(goal, intl)}
              {!isNewJustification && (
                <span className={styles.normalText}>
                  : {goal.justification}
                </span>
              )}
            </Text>
            {isNewJustification ? (
              <>
                <Text type='b300'>
                  <FormattedMessage
                    id='cs_perfportal_goal_justification_reason'
                    defaultMessage="Give details about why this goal wasn't met. Note challenges and blockers as well as next steps."
                  />
                </Text>
                <Textarea
                  value={addJustificationValue}
                  data-cy='comment-input'
                  onChange={setAddJustificationValue}
                  error={
                    addJustificationValue.length > GOAL_JUSTIFICATION_SIZE_LIMIT
                  }
                  errorMessage={
                    addJustificationValue.length > GOAL_JUSTIFICATION_SIZE_LIMIT
                      ? intl.formatMessage({
                          id: 'cs_perfportal_coaching_comment_comment_size_error',
                          defaultMessage:
                            'Comment must be under 500 characters.',
                        })
                      : ''
                  }
                  label={intl.formatMessage({
                    id: 'cs_perfportal_goal_justification_text_label',
                    defaultMessage: 'Add details here *',
                  })}
                />
                <Text type='b100'>
                  <FormattedMessage
                    id={'cs_perfportal_goal_justification_instruction'}
                    defaultMessage={`<emphasis>Note: </emphasis>Details can only be submitted once, and they can't be edited after they've been submitted.`}
                    values={{
                      emphasis: (chunks: ReactNode) => (
                        <span className={styles.emphasis}>{chunks}</span>
                      ),
                    }}
                  />
                </Text>
              </>
            ) : (
              <Text type='b100'>
                {intl.formatMessage(
                  {
                    id: 'cs_perfportal_goal_justification_added_details_on',
                    defaultMessage: '{user} added these details on {time}',
                  },
                  {
                    user: goal.justificationUserName,
                    time: getTimestampTranslation(goal.justificationTime),
                  }
                )}
              </Text>
            )}
          </Column>
          <ModalFooter>
            {isNewJustification && (
              <Row alignmentHorizontal='end'>
                <Button
                  type='secondary'
                  size='medium'
                  minWidth={150}
                  onClick={() => onClickClose()}
                  data-testid='cancel-details-button'
                  data-csa-c-slot-id={`${page}-goal-setting-cancel-details`}
                  data-csa-c-content-id={`${page}-goal-setting-cancel-details-for${goal.userName}-${goal.goalID}`}
                  {...commonClickstreamData}
                >
                  <FormattedMessage
                    id='cs_perfportal_cancel'
                    defaultMessage='Cancel'
                  />
                </Button>

                <Button
                  type='primary'
                  size='medium'
                  minWidth={150}
                  data-csa-c-slot-id={`${page}-goal-setting-submit-details`}
                  data-csa-c-content-id={`${page}-goal-setting-submit-details-for${goal.userName}-${goal.goalID}`}
                  {...commonClickstreamData}
                  data-testid='submit-details-button'
                  disabled={
                    addJustificationValue == '' ||
                    addJustificationValue.length >
                      GOAL_JUSTIFICATION_SIZE_LIMIT ||
                    createGoalJustificationState === DataState.LOADING
                  }
                  onClick={() => onClickAddJustificationButton()}
                >
                  {createGoalJustificationState === DataState.LOADING ? (
                    <Loader size='small' />
                  ) : (
                    <FormattedMessage
                      id='cs_perfportal_goal_justification_submit_details'
                      defaultMessage='Submit Details'
                      description='Send button'
                    />
                  )}
                </Button>
              </Row>
            )}
          </ModalFooter>
        </Box>
      </Modal>
    </>
  );
};

export default GoalJustificationModal;
